import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { contentAPI, eventBatchAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, FilterBar, EmptyState, StatusChip, DataTableCard } from '../components/ui';
import './ModuleAdmin.css';
import './HomeCategories.css';

const thSx = {
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#475569',
  bgcolor: '#f8fafc'
};

const emptyCategoryForm = {
  name: '',
  slug: '',
  sectionType: 'experience',
  description: '',
  hasSubcategories: false,
  isActive: true,
  order: 0
};

const emptySubcategoryForm = {
  name: '',
  slug: '',
  icon: '',
  description: '',
  isActive: true,
  order: 0
};

const HomeCategories = () => {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canEditSlugs = can('slug_manage');
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [subcategoryForm, setSubcategoryForm] = useState(emptySubcategoryForm);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [scopeSubcategoryId, setScopeSubcategoryId] = useState('');
  const [trekSearch, setTrekSearch] = useState('');
  const [selectedTrekIds, setSelectedTrekIds] = useState([]);
  const [activePanel, setActivePanel] = useState('none'); // 'none' | 'category' | 'subcategory'

  const categoriesQuery = useQuery(['home-categories-admin'], async () => {
    const res = await contentAPI.getHomeCategories({ includeInactive: 'true' });
    return res.data?.data || [];
  });

  const treksQuery = useQuery(['treks-for-home-categories'], async () => {
    // Only show treks that currently have upcoming event batches,
    // so admins can't map treks that would 404 / have no events.
    const res = await eventBatchAPI.listUpcomingTreks({ limit: 1000 });
    const items = res.data?.data || [];
    return items
      .map((row) => row.trek)
      .filter((trek) => trek && trek.id && trek.name);
  });

  const categories = categoriesQuery.data || [];
  const treks = treksQuery.data || [];

  useEffect(() => {
    if (!selectedCategoryId && categories.length > 0) {
      setSelectedCategoryId(String(categories[0].id));
    }
    if (selectedCategoryId && !categories.some((c) => String(c.id) === String(selectedCategoryId))) {
      setSelectedCategoryId(categories.length ? String(categories[0].id) : '');
    }
  }, [categories, selectedCategoryId]);

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(selectedCategoryId)) || null,
    [categories, selectedCategoryId]
  );

  const iconOptions = useMemo(() => {
    const base = [
      { value: '', label: 'No icon' },
      { value: 'snow', label: '❄️ Snow / Winter' },
      { value: 'car', label: '🚗 Road Trips' },
      { value: 'mountain', label: '🏔️ Mountains' },
      { value: 'camp', label: '🏕️ Camping' },
      { value: 'cycle', label: '🚴‍♂️ Cycling' }
    ];
    const seen = new Set(base.map((o) => o.value));
    categories.forEach((cat) => {
      (cat.subcategories || []).forEach((sub) => {
        if (sub.icon && !seen.has(sub.icon)) {
          seen.add(sub.icon);
          base.push({ value: sub.icon, label: sub.icon });
        }
      });
    });
    return base;
  }, [categories]);

  const getIconLabel = (value) => {
    if (!value) return 'No icon';
    const found = iconOptions.find((opt) => opt.value === value);
    return found ? found.label : value;
  };

  const visibleTreks = useMemo(() => {
    const q = trekSearch.trim().toLowerCase();
    if (!q) return treks;
    return treks.filter((trek) => String(trek.name || '').toLowerCase().includes(q));
  }, [treks, trekSearch]);

  const scopeOptions = useMemo(() => {
    if (!selectedCategory) return [];
    const subcats = Array.isArray(selectedCategory.subcategories) ? selectedCategory.subcategories : [];
    return [
      { value: '', label: 'Main category treks' },
      ...subcats.map((sub) => ({ value: String(sub.id), label: `Subcategory: ${sub.name}` }))
    ];
  }, [selectedCategory]);

  const assignedTreksQuery = useQuery(
    ['home-category-treks', selectedCategoryId, scopeSubcategoryId],
    async () => {
      const params = scopeSubcategoryId ? { subcategoryId: scopeSubcategoryId } : undefined;
      const res = await contentAPI.getHomeCategoryTreks(selectedCategoryId, params);
      return res.data?.data || [];
    },
    { enabled: !!selectedCategoryId }
  );

  useEffect(() => {
    const links = assignedTreksQuery.data || [];
    const ids = links.map((row) => Number(row.trekId || row.trek?.id)).filter(Boolean);
    setSelectedTrekIds(ids);
  }, [assignedTreksQuery.data]);

  const refreshAll = () => {
    queryClient.invalidateQueries(['home-categories-admin']);
    if (selectedCategoryId) {
      queryClient.invalidateQueries(['home-category-treks', selectedCategoryId, scopeSubcategoryId]);
    }
  };

  const createCategoryMutation = useMutation((payload) => contentAPI.createHomeCategory(payload), {
    onSuccess: () => {
      setCategoryForm(emptyCategoryForm);
      setEditingCategoryId(null);
      refreshAll();
    }
  });
  const updateCategoryMutation = useMutation(({ id, payload }) => contentAPI.updateHomeCategory(id, payload), {
    onSuccess: () => {
      setCategoryForm(emptyCategoryForm);
      setEditingCategoryId(null);
      refreshAll();
    }
  });
  const deleteCategoryMutation = useMutation((id) => contentAPI.deleteHomeCategory(id), {
    onSuccess: () => refreshAll()
  });

  const createSubcategoryMutation = useMutation(
    ({ categoryId, payload }) => contentAPI.createHomeSubcategory(categoryId, payload),
    {
      onSuccess: () => {
        setSubcategoryForm(emptySubcategoryForm);
        setEditingSubcategoryId(null);
        refreshAll();
      }
    }
  );
  const updateSubcategoryMutation = useMutation(
    ({ categoryId, subcategoryId, payload }) => contentAPI.updateHomeSubcategory(categoryId, subcategoryId, payload),
    {
      onSuccess: () => {
        setSubcategoryForm(emptySubcategoryForm);
        setEditingSubcategoryId(null);
        refreshAll();
      }
    }
  );
  const deleteSubcategoryMutation = useMutation(
    ({ categoryId, subcategoryId }) => contentAPI.deleteHomeSubcategory(categoryId, subcategoryId),
    { onSuccess: () => refreshAll() }
  );

  const saveTreksMutation = useMutation(
    ({ categoryId, payload }) => contentAPI.setHomeCategoryTreks(categoryId, payload),
    {
      onSuccess: () => {
        if (selectedCategoryId) {
          queryClient.invalidateQueries(['home-category-treks', selectedCategoryId, scopeSubcategoryId]);
        }
      }
    }
  );

  const onCategorySubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...categoryForm,
      order: Number(categoryForm.order || 0),
      hasSubcategories: !!categoryForm.hasSubcategories,
      isActive: !!categoryForm.isActive
    };
    if (editingCategoryId) {
      updateCategoryMutation.mutate({ id: editingCategoryId, payload });
    } else {
      createCategoryMutation.mutate(payload);
    }
  };

  const onSubcategorySubmit = (e) => {
    e.preventDefault();
    if (!selectedCategoryId) return;
    const payload = {
      ...subcategoryForm,
      order: Number(subcategoryForm.order || 0),
      isActive: !!subcategoryForm.isActive
    };
    if (editingSubcategoryId) {
      updateSubcategoryMutation.mutate({
        categoryId: selectedCategoryId,
        subcategoryId: editingSubcategoryId,
        payload
      });
    } else {
      createSubcategoryMutation.mutate({ categoryId: selectedCategoryId, payload });
    }
  };

  return (
    <div className="module-page homecat-page">
      <PageHeader
        title="Home Categories"
        subtitle="Create Home page sections, optional capsule tabs, and map treks to each scope."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setActivePanel('category');
                setEditingCategoryId(null);
                setCategoryForm(emptyCategoryForm);
              }}
            >
              Add Main Category
            </Button>
            {selectedCategory ? (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => {
                  setActivePanel('subcategory');
                  setEditingSubcategoryId(null);
                  setSubcategoryForm(emptySubcategoryForm);
                }}
              >
                Add Subcategory
              </Button>
            ) : null}
          </>
        }
      />

      <Card className="homecat-list-card" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} className="homecat-card-title">
            Main Categories
          </Typography>
          <Typography variant="body2" color="text.secondary" className="homecat-card-subtitle" sx={{ mb: 2 }}>
            Pick a category, then use the panels below to add or edit details.
          </Typography>
          <DataTableCard sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
            {categoriesQuery.isLoading ? (
              <div className="ops-loading-box">
                <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary">Loading categories…</Typography>
              </div>
            ) : categories.length === 0 ? (
              <EmptyState title="No categories yet" description="Add a main category to configure home page sections." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>Name</TableCell>
                    <TableCell sx={thSx}>Slug</TableCell>
                    <TableCell sx={thSx}>Type</TableCell>
                    <TableCell sx={thSx}>Subcategories</TableCell>
                    <TableCell sx={thSx}>Status</TableCell>
                    <TableCell sx={thSx}>Order</TableCell>
                    <TableCell align="right" sx={thSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      className={String(selectedCategoryId) === String(item.id) ? 'homecat-row-selected' : ''}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                      <TableCell>{item.slug}</TableCell>
                      <TableCell>{item.sectionType}</TableCell>
                      <TableCell>{Array.isArray(item.subcategories) ? item.subcategories.length : 0}</TableCell>
                      <TableCell>
                        <StatusChip status={item.isActive ? 'active' : 'inactive'} label={item.isActive ? 'Active' : 'Inactive'} />
                      </TableCell>
                      <TableCell>{item.order || 0}</TableCell>
                      <TableCell align="right" className="module-actions">
                        <button
                          className="module-link-btn"
                          type="button"
                          onClick={() => {
                            setSelectedCategoryId(String(item.id));
                            setActivePanel('subcategory');
                          }}
                        >
                          Manage
                        </button>
                        <button
                          className="module-link-btn"
                          type="button"
                          onClick={() => {
                            setEditingCategoryId(item.id);
                            setCategoryForm({
                              name: item.name || '',
                              slug: item.slug || '',
                              sectionType: item.sectionType || 'experience',
                              description: item.description || '',
                              hasSubcategories: !!item.hasSubcategories,
                              isActive: !!item.isActive,
                              order: item.order || 0
                            });
                            setActivePanel('category');
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="module-link-btn danger"
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Delete category "${item.name}"?`)) {
                              deleteCategoryMutation.mutate(item.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DataTableCard>
        </CardContent>
      </Card>

      {activePanel === 'category' && (
        <Card component="form" className="homecat-form-card" onSubmit={onCategorySubmit} sx={{ mb: 2 }}>
          <CardContent className="module-form-grid">
          <div className="homecat-card-head">
            <h3>{editingCategoryId ? 'Edit Main Category' : 'Create Main Category'}</h3>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setEditingCategoryId(null);
                setCategoryForm(emptyCategoryForm);
                setActivePanel('none');
              }}
            >
              Close
            </Button>
          </div>
          <div>
            <label>Category Name</label>
            <input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <label>Slug</label>
            <input
              value={categoryForm.slug}
              onChange={(e) => setCategoryForm((p) => ({ ...p, slug: e.target.value }))}
              required
              disabled={!canEditSlugs}
            />
          </div>
          <div>
            <label>Section Type</label>
            <select
              value={categoryForm.sectionType}
              onChange={(e) => setCategoryForm((p) => ({ ...p, sectionType: e.target.value }))}
            >
              <option value="experience">experience</option>
              <option value="special">special</option>
            </select>
          </div>
          <div>
            <label>Order</label>
            <input
              type="number"
              value={categoryForm.order}
              onChange={(e) => setCategoryForm((p) => ({ ...p, order: e.target.value }))}
            />
          </div>
          <div className="homecat-full-row">
            <label>Description</label>
            <textarea
              rows="2"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div>
            <label>Has Subcategories</label>
            <select
              value={String(categoryForm.hasSubcategories)}
              onChange={(e) => setCategoryForm((p) => ({ ...p, hasSubcategories: e.target.value === 'true' }))}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
          <div>
            <label>Status</label>
            <select
              value={String(categoryForm.isActive)}
              onChange={(e) => setCategoryForm((p) => ({ ...p, isActive: e.target.value === 'true' }))}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="homecat-form-actions">
            <Button
              variant="contained"
              type="submit"
              disabled={createCategoryMutation.isLoading || updateCategoryMutation.isLoading}
            >
              {editingCategoryId ? 'Update Category' : 'Create Category'}
            </Button>
          </div>
          </CardContent>
        </Card>
      )}

      {activePanel === 'subcategory' && selectedCategory ? (
        <Card className="homecat-subcat-mapping">
          <CardContent>
          <div className="homecat-card-head">
            <h3>Subcategories & Trek Mapping for {selectedCategory.name}</h3>
            <button
              type="button"
              className="module-btn secondary"
              onClick={() => {
                setEditingSubcategoryId(null);
                setSubcategoryForm(emptySubcategoryForm);
                setActivePanel('none');
              }}
            >
              Close
            </button>
          </div>

          <div className="homecat-subcat-grid">
            <div className="homecat-subcategory-card">
              <h4 className="homecat-section-title">Subcategories</h4>
              <form className="module-form-grid" onSubmit={onSubcategorySubmit}>
                <div>
                  <label>Name</label>
                  <input
                    value={subcategoryForm.name}
                    onChange={(e) => setSubcategoryForm((p) => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label>Slug</label>
                  <input
                    value={subcategoryForm.slug}
                    onChange={(e) => setSubcategoryForm((p) => ({ ...p, slug: e.target.value }))}
                    required
                    disabled={!canEditSlugs}
                  />
              </div>
              <div>
                <label>Icon (optional)</label>
                <select
                  value={subcategoryForm.icon || ''}
                  onChange={(e) => setSubcategoryForm((p) => ({ ...p, icon: e.target.value }))}
                >
                  {iconOptions.map((opt) => (
                    <option key={opt.value || 'none'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
                <div>
                  <label>Order</label>
                  <input
                    type="number"
                    value={subcategoryForm.order}
                    onChange={(e) => setSubcategoryForm((p) => ({ ...p, order: e.target.value }))}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Description</label>
                  <textarea
                    rows="2"
                    value={subcategoryForm.description}
                    onChange={(e) => setSubcategoryForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
                <div>
                  <label>Status</label>
                  <select
                    value={String(subcategoryForm.isActive)}
                    onChange={(e) => setSubcategoryForm((p) => ({ ...p, isActive: e.target.value === 'true' }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'end', gap: '0.5rem' }}>
                  <button
                    className="module-btn"
                    type="submit"
                    disabled={createSubcategoryMutation.isLoading || updateSubcategoryMutation.isLoading}
                  >
                    {editingSubcategoryId ? 'Update Subcategory' : 'Create Subcategory'}
                  </button>
                  {editingSubcategoryId ? (
                    <button
                      type="button"
                      className="module-btn secondary"
                      onClick={() => {
                        setEditingSubcategoryId(null);
                        setSubcategoryForm(emptySubcategoryForm);
                      }}
                    >
                      Cancel Edit
                    </button>
                  ) : null}
                </div>
              </form>

              <DataTableCard sx={{ mt: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }} className="homecat-subcategory-table">
              {(selectedCategory.subcategories || []).length === 0 ? (
                <EmptyState title="No subcategories" description="Create a subcategory using the form above." />
              ) : (
              <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Name</TableCell>
                  <TableCell sx={thSx}>Slug</TableCell>
                  <TableCell sx={thSx}>Icon</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thSx}>Order</TableCell>
                  <TableCell align="right" sx={thSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedCategory.subcategories || []).map((sub) => (
                  <TableRow key={sub.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{sub.name}</TableCell>
                    <TableCell>{sub.slug}</TableCell>
                    <TableCell>{getIconLabel(sub.icon)}</TableCell>
                    <TableCell>
                      <StatusChip status={sub.isActive ? 'active' : 'inactive'} label={sub.isActive ? 'Active' : 'Inactive'} />
                    </TableCell>
                    <TableCell>{sub.order || 0}</TableCell>
                    <TableCell align="right" className="module-actions">
                      <button
                        className="module-link-btn"
                        onClick={() => {
                          setEditingSubcategoryId(sub.id);
                          setSubcategoryForm({
                            name: sub.name || '',
                            slug: sub.slug || '',
                            icon: sub.icon || '',
                            description: sub.description || '',
                            isActive: !!sub.isActive,
                            order: sub.order || 0
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="module-link-btn danger"
                        onClick={() => {
                          if (window.confirm(`Delete subcategory "${sub.name}"?`)) {
                            deleteSubcategoryMutation.mutate({
                              categoryId: selectedCategory.id,
                              subcategoryId: sub.id
                            });
                          }
                        }}
                      >
                        Delete
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
              )}
              </DataTableCard>
            </div>

            <div className="homecat-mapping-card">
              <h4 className="homecat-section-title">Trek Mapping</h4>
              <p className="module-subtitle homecat-mapping-hint">
                Assign treks to the main category or one specific subcategory.
              </p>
              <FilterBar sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }} contentSx={{ py: 1.5 }}>
                <div style={{ minWidth: 180, flex: '1 1 180px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Scope</label>
                  <select
                    value={scopeSubcategoryId}
                    onChange={(e) => setScopeSubcategoryId(e.target.value)}
                    className="homecat-mapping-select module-filter-select"
                    style={{ width: '100%' }}
                  >
                    {scopeOptions.map((option) => (
                      <option key={option.value || 'root'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <TextField
                  size="small"
                  label="Search trek"
                  placeholder="Type trek name…"
                  value={trekSearch}
                  onChange={(e) => setTrekSearch(e.target.value)}
                  sx={{ minWidth: 200, flex: '1 1 200px' }}
                />
              </FilterBar>

              <div className="homecat-mapping-list">
                {visibleTreks.map((trek) => {
                  const checked = selectedTrekIds.includes(Number(trek.id));
                  return (
                    <label key={trek.id} className="events-check homecat-mapping-item">
                      <span>{trek.name}</span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const id = Number(trek.id);
                          setSelectedTrekIds((prev) =>
                            e.target.checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
                          );
                        }}
                      />
                    </label>
                  );
                })}
              </div>

              <div className="homecat-mapping-actions">
                <Button
                  variant="contained"
                  type="button"
                  disabled={!selectedCategoryId || saveTreksMutation.isLoading}
                  onClick={() => {
                    saveTreksMutation.mutate({
                      categoryId: selectedCategoryId,
                      payload: {
                        subcategoryId: scopeSubcategoryId || null,
                        trekIds: selectedTrekIds
                      }
                    });
                  }}
                >
                  Save Trek Mapping
                </Button>
                <Button
                  variant="outlined"
                  type="button"
                  onClick={() => {
                    const links = assignedTreksQuery.data || [];
                    setSelectedTrekIds(links.map((row) => Number(row.trekId || row.trek?.id)).filter(Boolean));
                  }}
                >
                  Reset Selection
                </Button>
              </div>
            </div>
          </div>
          </CardContent>
        </Card>
      ) : null}
      {activePanel === 'subcategory' && !selectedCategory ? (
        <Card className="homecat-empty-state">
          <CardContent>
            <EmptyState title="Select a category" description="Pick a main category from the list above to add subcategories and treks." />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default HomeCategories;
